import { useEffect, useMemo, useState } from 'react';
import {
  EVENT_META,
  FEED_TYPES,
  activeMinutes,
  breastSides,
  can,
  formatClock,
  formatDuration,
  localDate,
  milestoneToday,
  type BabyEvent,
  type CustomKind,
  type EventCategory,
  type EventType,
} from '@bb/shared/core';
import { api } from '../api';
import { Icon, TYPE_ICON, useNow } from '../components';
import { useApp } from '../state';
import { ChildHeader } from './ChildHeader';
import { Confetti } from './Confetti';
import { Day24 } from './Day24';
import { RunningPanel } from './Running';
import { TimerBar, useOutOfView } from './TimerBar';

/** Все действия; порядок и скрытые — в настройках пользователя. Свои события семьи — ключи `custom:<id>`. */
export const ALL_ACTIONS: EventType[] = ['breast', 'bottle', 'sleep', 'diaper', 'walk', 'bath', 'gym', 'cry', 'activity', 'solid', 'medicine', 'temperature', 'measure', 'note'];

export interface ActionItem {
  key: string;
  type: EventType;
  label: string;
  emoji?: string;
  cat: EventCategory;
  kind?: CustomKind;
}

function useActions() {
  const { me, family } = useApp();
  const items = new Map<string, ActionItem>();
  for (const t of ALL_ACTIONS) items.set(t, { key: t, type: t, label: EVENT_META[t].label, cat: EVENT_META[t].category });
  for (const k of family.settings?.customKinds ?? []) items.set(`custom:${k.id}`, { key: `custom:${k.id}`, type: 'custom', label: k.name, emoji: k.emoji, cat: k.cat, kind: k });
  const order = me.user.settings?.actions ?? [];
  const hidden = new Set(me.user.settings?.hiddenActions ?? []);
  const keys = [...order.filter((a) => items.has(a)), ...[...items.keys()].filter((a) => !order.includes(a))];
  return { ordered: keys.map((k) => items.get(k)!), hidden };
}

function ActionIcon({ a, size }: { a: ActionItem; size: number }) {
  return a.emoji ? (
    <span className="emoji-icon" style={{ fontSize: size * 0.85, lineHeight: 1 }} aria-hidden="true">
      {a.emoji}
    </span>
  ) : (
    <Icon name={TYPE_ICON[a.type]} size={size} />
  );
}

const ago = (ms: number) => {
  const m = (Date.now() - ms) / 60000;
  return m < 1 ? 'только что' : `${formatDuration(m)} назад`;
};

function lastOf(events: readonly BabyEvent[], pred: (e: BabyEvent) => boolean): BabyEvent | null {
  let best: BabyEvent | null = null;
  for (const e of events) if (pred(e) && (!best || Date.parse(e.startedAt) > Date.parse(best.startedAt))) best = e;
  return best;
}

export function Today() {
  const ctx = useApp();
  const { child, family, running, recent } = ctx;
  const tick = useNow(30000);
  const now = Math.max(tick, recent ? Date.now() : tick);
  const canWrite = can(family.role, 'events.write');
  const today = localDate(now, family.tz);
  const all = useMemo(() => [...(recent?.events ?? []), ...(recent?.last ?? [])], [recent]);

  /* ---------- Праздник ---------- */
  const party = milestoneToday(child.birthDate, today, child.name);
  const [boom, setBoom] = useState(false);
  useEffect(() => {
    if (!party) return;
    const key = `party:${child.id}:${today}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* без хранилища — салют каждый раз */
    }
    setBoom(true);
  }, [party?.title, child.id, today]);

  /* ---------- Плашки ---------- */
  const lastFeed = lastOf(all, (e) => FEED_TYPES.includes(e.type) && !!e.endedAt);
  const lastDiaper = lastOf(all, (e) => e.type === 'diaper');
  const lastSleep = lastOf(all, (e) => e.type === 'sleep');
  const sleeping = running.find((e) => e.type === 'sleep');
  const feeds24 = (recent?.events ?? []).filter((e) => FEED_TYPES.includes(e.type) && Date.parse(e.startedAt) > now - 24 * 3600000).map((e) => Date.parse(e.startedAt)).sort((a, b) => a - b);
  const gaps = feeds24.slice(1).map((t, i) => t - feeds24[i]!);
  const avgGap = gaps.length >= 2 ? gaps.reduce((s, g) => s + g, 0) / gaps.length : null;
  const nextFeed = avgGap && lastFeed ? Date.parse(lastFeed.startedAt) + avgGap : null;

  const feedDetail = (e: BabyEvent) => {
    if (e.type === 'breast') {
      const s = breastSides(e);
      const side = s.left > 0.5 && s.right > 0.5 ? 'обе' : s.last === 'right' ? 'правая' : 'левая';
      return `грудь · ${side} · ${formatDuration(activeMinutes(e))}`;
    }
    if (e.type === 'bottle') return `бутылочка · ${String(e.payload.ml)} мл`;
    return `прикорм · ${String(e.payload.food ?? '')}`;
  };

  const { ordered, hidden } = useActions();
  const [editing, setEditing] = useState(false);
  const [runRef, runOut] = useOutOfView<HTMLDivElement>();

  return (
    <div className="stack">
      {/* Шапка закреплена; при прокрутке под ней — компактная карточка таймера */}
      <div className="sticky-top">
        <ChildHeader
          big
          right={
            <button type="button" className="icon-btn" aria-label="Настройки" onClick={ctx.openSettings}>
              <Icon name="settings" size={20} />
            </button>
          }
        />
        {runOut && running.length > 0 && <TimerBar floating />}
      </div>

      {party && (
        <button type="button" className="party" onClick={() => setBoom(true)} data-testid="party">
          <span className="party-emoji" aria-hidden="true">
            {party.emoji}
          </span>
          <span className="grow" style={{ textAlign: 'left' }}>
            <b>{party.title}</b>
            <span className="tiny" style={{ display: 'block' }}>
              Нажмите — ещё салют
            </span>
          </span>
        </button>
      )}
      {boom && <Confetti onDone={() => setBoom(false)} />}

      {!canWrite && <div className="banner">Вы смотрите дневник как {family.role === 'relative' ? 'родственник' : 'гость'} — добавлять записи могут родители и няня.</div>}

      {running.length > 0 && (
        <div ref={runRef} className="stack">
          {running.map((e) => (
            <RunningPanel key={e.id} e={e} canWrite={canWrite} />
          ))}
        </div>
      )}

      {!recent ? (
        <>
          <div className="tiles2">
            <div className="skeleton" />
            <div className="skeleton" />
          </div>
          <div className="skeleton" style={{ minHeight: 300 }} />
        </>
      ) : (
        <>
          <div className="tiles2">
            <button type="button" className="stat cat-feed tap" onClick={() => ctx.openAction(lastFeed?.type === 'bottle' ? 'bottle' : 'breast')}>
              <span className="label">Кормление</span>
              <span className="value" data-testid="last-feed">
                {lastFeed ? ago(Date.parse(lastFeed.endedAt!)) : '—'}
              </span>
              <span className="tiny">{lastFeed ? `${formatClock(Date.parse(lastFeed.startedAt), family.tz)} · ${feedDetail(lastFeed)}` : 'записей ещё нет'}</span>
            </button>
            <button type="button" className="stat cat-sleep tap" onClick={() => ctx.openAction('sleep', sleeping)}>
              <span className="label">{sleeping?.payload.settling ? 'Укладываем' : sleeping ? 'Спит' : 'Не спит'}</span>
              <span className="value" data-testid="awake">
                {sleeping ? formatDuration(activeMinutes(sleeping, now)) : lastSleep?.endedAt ? formatDuration((now - Date.parse(lastSleep.endedAt)) / 60000) : '—'}
              </span>
              <span className="tiny">{sleeping?.payload.settling ? `начали в ${formatClock(Date.parse(sleeping.startedAt), family.tz)}` : sleeping ? `уснул в ${formatClock(Date.parse(sleeping.startedAt), family.tz)}` : lastSleep?.endedAt ? `проснулся в ${formatClock(Date.parse(lastSleep.endedAt), family.tz)}` : 'сна ещё не было'}</span>
            </button>
            <button type="button" className="stat cat-diaper tap" onClick={() => ctx.openEditor({ type: 'diaper' })}>
              <span className="label">Подгузник</span>
              <span className="value">{lastDiaper ? ago(Date.parse(lastDiaper.startedAt)) : '—'}</span>
              <span className="tiny">{lastDiaper ? `${formatClock(Date.parse(lastDiaper.startedAt), family.tz)}` : 'записей ещё нет'}</span>
            </button>
            <div className="stat cat-other">
              <span className="label">Следующее кормление</span>
              <span className="value">{nextFeed ? (nextFeed < now ? 'уже пора' : `≈ ${formatClock(nextFeed, family.tz)}`) : '—'}</span>
              <span className="tiny">{avgGap ? `в среднем каждые ${formatDuration(avgGap / 60000)}` : 'посчитаю после нескольких кормлений'}</span>
            </div>
          </div>

          <Day24 />
        </>
      )}

      {canWrite && (
        <section className="card stack" aria-label="Добавить">
          <div className="row">
            <span className="section-title grow">Добавить</span>
            <button type="button" className="link-btn" onClick={() => setEditing(!editing)} data-testid="edit-actions">
              {editing ? 'Готово' : 'Порядок'}
            </button>
          </div>
          {editing ? (
            <ActionsEditor ordered={ordered} hidden={hidden} />
          ) : (
            <div className="tiles3">
              {ordered
                .filter((a) => !hidden.has(a.key))
                .map((a) => {
                  const t = a.type;
                  const run = running.find((e) => e.type === t && (!a.kind || e.payload.kindId === a.kind.id));
                  const cat = a.cat;
                  const label = run ? (t === 'sleep' ? (run.payload.settling ? 'Уснул?' : 'Проснулся') : t === 'walk' ? 'Домой' : 'Идёт…') : a.label;
                  return (
                    <button
                      key={a.key}
                      type="button"
                      className={`tile ${cat === 'other' ? 'plain' : `cat-${cat}`}${run ? ' live' : ''}`}
                      onClick={() =>
                        a.kind && !run
                          ? ctx.openEditor({ type: 'custom', preset: { kindId: a.kind.id, name: a.kind.name, cat: a.kind.cat, ...(a.kind.unit ? { unit: a.kind.unit } : {}) } })
                          : ctx.openAction(t, run)
                      }
                      data-testid={`act-${a.key.replace(':', '-')}`}
                    >
                      <ActionIcon a={a} size={26} />
                      {label}
                    </button>
                  );
                })}
            </div>
          )}
        </section>
      )}

      <button type="button" className="card row promo" onClick={ctx.openProgress}>
        <Icon name="progress" />
        <span className="grow" style={{ textAlign: 'left' }}>
          <span style={{ display: 'block', fontWeight: 600 }}>Прогресс и статистика</span>
          <span className="small muted">Графики сна, кормлений и роста за неделю, месяц, год</span>
        </span>
        <Icon name="right" size={18} />
      </button>
    </div>
  );
}

/** Порядок кнопок: стрелки вверх/вниз и «показывать/скрыть». Сохраняется у пользователя на сервере. */
function ActionsEditor({ ordered, hidden }: { ordered: ActionItem[]; hidden: Set<string> }) {
  const ctx = useApp();
  const [list, setList] = useState(ordered);
  const [off, setOff] = useState(new Set(hidden));
  const save = async (l: ActionItem[], o: Set<string>) => {
    setList(l);
    setOff(o);
    try {
      await api.saveUserSettings({ actions: l.map((a) => a.key), hiddenActions: [...o] });
      await ctx.reloadMe();
    } catch (e) {
      ctx.fail(e);
    }
  };
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const l = [...list];
    [l[i], l[j]] = [l[j]!, l[i]!];
    void save(l, off);
  };
  return (
    <ul className="plain-list order-list">
      {list.map((a, i) => (
        <li key={a.key} className={off.has(a.key) ? 'off' : ''}>
          <ActionIcon a={a} size={20} />
          <span className="grow">{a.label}</span>
          <button type="button" className="icon-btn sm" aria-label={`${a.label}: выше`} disabled={i === 0} onClick={() => move(i, -1)}>
            <Icon name="up" size={16} />
          </button>
          <button type="button" className="icon-btn sm" aria-label={`${a.label}: ниже`} disabled={i === list.length - 1} onClick={() => move(i, 1)}>
            <Icon name="down" size={16} />
          </button>
          <button
            type="button"
            className="icon-btn sm"
            aria-label={off.has(a.key) ? `Показывать «${a.label}»` : `Скрыть «${a.label}»`}
            onClick={() => {
              const o = new Set(off);
              if (o.has(a.key)) o.delete(a.key);
              else o.add(a.key);
              void save(list, o);
            }}
          >
            <Icon name={off.has(a.key) ? 'eyeoff' : 'eye'} size={16} />
          </button>
        </li>
      ))}
    </ul>
  );
}
